import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';

/**
 * BarcodeInputContext
 *
 * 提供全域條碼輸入功能，任何頁面只需使用 useBarcodeInput hook 即可自動監聽鍵盤輸入。
 *
 * 特性：
 * - 全域鍵盤監聽，自動導向條碼輸入
 * - 當用戶在其他 input/textarea 中輸入時不攔截
 * - 支援多個 handler 同時註冊（依序呼叫）
 * - 支援 handler 優先級
 */

const BarcodeInputContext = createContext();

export const BarcodeInputProvider = ({ children }) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const handlersRef = useRef([]);

  // 註冊 handler（返回 unregister 函數）
  const registerHandler = useCallback((handler, options = {}) => {
    const { priority = 0 } = options;
    const handlerEntry = { handler, priority };

    handlersRef.current = [...handlersRef.current, handlerEntry]
      .sort((a, b) => b.priority - a.priority); // 優先級高的排前面

    // 返回 unregister 函數
    return () => {
      handlersRef.current = handlersRef.current.filter(entry => entry !== handlerEntry);
    };
  }, []);

  // 提交條碼
  const submitBarcode = useCallback(async (code) => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) return;

    // 依序呼叫所有 handlers
    for (const { handler } of handlersRef.current) {
      try {
        const result = await handler(trimmedCode);
        // 如果 handler 返回 true，表示已處理，停止傳遞
        if (result === true) break;
      } catch (err) {
        console.error('[BarcodeInputContext] Handler error:', err);
      }
    }
  }, []);

  // 清空輸入
  const clear = useCallback(() => {
    setInputValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  // Focus 到輸入框
  const focus = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  // 全域 keydown 監聽器
  useEffect(() => {
    const handleKeyDown = (event) => {
      const input = inputRef.current;
      if (!input) return;

      const target = event.target;

      // 如果已經是 input 本身，不處理
      if (target === input) return;

      // 如果用戶正在其他輸入框輸入，不攔截
      const tagName = target.tagName.toLowerCase();
      const isEditable =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target.isContentEditable;

      if (isEditable) return;

      // 檢查是否為貼上快捷鍵（Cmd+V / Ctrl+V）
      const isPaste = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v';

      // 排除帶有修飾鍵的組合鍵，但允許貼上
      if ((event.metaKey || event.ctrlKey || event.altKey) && !isPaste) return;

      // Focus 到 input
      input.focus();

      // 處理貼上 - 讓瀏覽器原生處理
      if (isPaste) return;

      // 處理 Enter - 提交
      if (event.key === 'Enter') {
        event.preventDefault();
        if (input.value) {
          submitBarcode(input.value);
          input.value = '';
          setInputValue('');
        }
        return;
      }

      // 處理可列印字元
      const isPrintableKey = event.key?.length === 1;
      if (isPrintableKey) {
        event.preventDefault();
        const nextValue = `${input.value}${event.key}`;
        input.value = nextValue;
        setInputValue(nextValue);
        input.setSelectionRange(nextValue.length, nextValue.length);
        return;
      }

      // 處理 Backspace
      if (event.key === 'Backspace') {
        event.preventDefault();
        const nextValue = input.value.slice(0, -1);
        input.value = nextValue;
        setInputValue(nextValue);
        input.setSelectionRange(nextValue.length, nextValue.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [submitBarcode]);

  const value = {
    inputRef,
    inputValue,
    registerHandler,
    submitBarcode,
    clear,
    focus,
    hasHandler: handlersRef.current.length > 0,
  };

  return (
    <BarcodeInputContext.Provider value={value}>
      {children}
    </BarcodeInputContext.Provider>
  );
};

/**
 * useBarcodeInput hook
 *
 * 註冊條碼處理器，當有條碼輸入時會自動呼叫。
 *
 * @param {Function} handler - 條碼處理函數 (code: string) => void | Promise<void> | boolean
 *                             返回 true 表示已處理，停止傳遞給其他 handler
 * @param {Array} deps - 依賴陣列
 * @param {Object} options - 選項
 * @param {number} options.priority - 優先級（數字越大越優先，預設 0）
 *
 * @example
 * function QcPage() {
 *   const handleBarcode = useCallback(async (code) => {
 *     console.log('Scanned:', code);
 *     return true; // 已處理，不傳遞給其他 handler
 *   }, []);
 *
 *   useBarcodeInput(handleBarcode);
 *
 *   return <div>...</div>;
 * }
 */
export const useBarcodeInput = (handler, deps = [], options = {}) => {
  const context = useContext(BarcodeInputContext);

  if (!context) {
    throw new Error('useBarcodeInput must be used within a BarcodeInputProvider');
  }

  const { registerHandler, clear, focus } = context;

  // ✅ 使用 ref 保存最新的 handler，避免 useEffect 時序間隙導致舊 handler 被呼叫
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // 穩定的 wrapper：永遠委派到最新的 handler ref
  const stableHandler = useMemo(() => {
    if (!handler) return null;
    return (...args) => handlerRef.current(...args);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!handler]); // 只在 handler 從 null → non-null 或反過來時改變

  useEffect(() => {
    if (!stableHandler) return;

    const unregister = registerHandler(stableHandler, options);
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableHandler]);

  return { clear, focus };
};

/**
 * useBarcodeInputContext hook
 *
 * 取得完整的 BarcodeInput context（用於需要更多控制的場景）
 */
export const useBarcodeInputContext = () => {
  const context = useContext(BarcodeInputContext);

  if (!context) {
    throw new Error('useBarcodeInputContext must be used within a BarcodeInputProvider');
  }

  return context;
};

export default BarcodeInputContext;
